import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { useState, type FunctionComponent, type ReactNode } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export interface TabItem {
  key: string;
  label: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeKey?: string;
  defaultActiveKey?: string;
  onChange?: (key: string) => void;
  /** Fires on every tab click, including the already-active tab. */
  onTabClick?: (key: string) => void;
  size?: "small" | "middle" | "large";
  /** Unmount inactive panels instead of keeping them mounted (hidden) after their first visit. */
  destroyOnHidden?: boolean;
  className?: string;
  id?: string;
}

const tabSizeClass = {
  small: "py-2 text-sm",
  middle: "py-3 text-sm",
  large: "py-4 text-base",
} as const;

interface LazyTabBodyProps {
  active: boolean;
  children: ReactNode;
}

/** Defers rendering until the tab is first active, then keeps the body mounted (antd default). */
const LazyTabBody: FunctionComponent<LazyTabBodyProps> = ({ active, children }) => {
  const [visited, setVisited] = useState(active);
  if (active && !visited) setVisited(true);

  return visited ? children : null;
};

/** Horizontal tabs with an ink bar (antd `Tabs` with `items`). */
export const Tabs: FunctionComponent<TabsProps> = ({
  items,
  activeKey,
  defaultActiveKey,
  onChange,
  onTabClick,
  size = "middle",
  destroyOnHidden,
  className,
  id,
}) => {
  const [internalKey, setInternalKey] = useState(defaultActiveKey ?? items[0]?.key);
  const currentKey = activeKey ?? internalKey;

  return (
    <BaseTabs.Root
      value={activeKey}
      defaultValue={activeKey === undefined ? (defaultActiveKey ?? items[0]?.key) : undefined}
      onValueChange={(value) => {
        const key = String(value);
        setInternalKey(key);
        onChange?.(key);
      }}
      className={cn("flex flex-col", className)}
      id={id}
    >
      <BaseTabs.List className="relative mb-4 flex gap-8 border-b border-noa">
        {items.map((item) => (
          <BaseTabs.Tab
            key={item.key}
            value={item.key}
            disabled={item.disabled}
            onClick={() => onTabClick?.(item.key)}
            className={cn(
              "cursor-pointer text-noa-muted transition-colors hover:text-noa-strong",
              // An icon in a label sits inline with its text, as antd's did; the base styles make every svg a block
              "[&_svg]:inline-block [&_svg]:align-[-0.125em]",
              "data-[active]:text-noa-strong",
              FOCUS_RING,
              "data-[disabled]:cursor-not-allowed data-[disabled]:text-noa-disabled",
              tabSizeClass[size]
            )}
          >
            {item.label}
          </BaseTabs.Tab>
        ))}
        <BaseTabs.Indicator className="absolute bottom-[-1px] left-[var(--active-tab-left)] h-0.5 w-[var(--active-tab-width)] bg-noa-primary transition-all" />
      </BaseTabs.List>
      {items.map((item) => (
        <BaseTabs.Panel key={item.key} value={item.key} keepMounted={!destroyOnHidden} className="outline-none">
          <LazyTabBody active={item.key === currentKey}>{item.children}</LazyTabBody>
        </BaseTabs.Panel>
      ))}
    </BaseTabs.Root>
  );
};
